/**
 * ComposerPlusMenu — the composer's "+" button (replaces the paperclip),
 * opening an Anthropic-style menu: "Add files or photos" plus a nested
 * **Connectors** submenu of per-conversation external-MCP toggles.
 *
 * Two-level connector model:
 *   • Settings (mcp_servers.enabled) = "connect & load tools" — only connected,
 *     non-builtin servers appear here.
 *   • This menu (conversations.enabled_connectors) = "include those tools in
 *     THIS chat". Internal MCPs (cowork's own tools + the Nbyula gateway) are
 *     always on and built-in, so they are NEVER shown here.
 *
 * Shared by ChatInput (active chat → persist immediately) and HomeChatInput
 * (new chat → ``persistConnectors=false``; the picks ride along on
 * createConversation). Toggling re-primes the conversation's prompt cache
 * server-side, so it's a deliberate per-conversation choice.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useChatStore } from "@/stores/chatStore";
import { useMcpStore } from "@/stores/mcpStore";
import { useNavStore } from "@/stores/navStore";
import { Spinner } from "@/components/common/Spinner";

export function ComposerPlusMenu({
  onAddFiles,
  persistConnectors = true,
}: {
  onAddFiles: () => void;
  persistConnectors?: boolean;
}) {
  const enabled = useChatStore((s) => s.enabledConnectors);
  const setEnabled = useChatStore((s) => s.setEnabledConnectors);
  const servers = useMcpStore((s) => s.servers);
  const isLoading = useMcpStore((s) => s.isLoading);
  const loadServers = useMcpStore((s) => s.loadServers);
  const navigate = useNavStore((s) => s.navigate);

  const [open, setOpen] = useState(false);
  const [subOpen, setSubOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Opt-in candidates: connected (settings-enabled) AND not built-in. Built-in
  // servers (cowork tools, Nbyula gateway) are always on and excluded.
  const connectors = useMemo(
    () => servers.filter((s) => s.enabled && !s.builtin),
    [servers],
  );

  useEffect(() => {
    if (open) loadServers();
  }, [open, loadServers]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setSubOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const close = () => {
    setOpen(false);
    setSubOpen(false);
  };

  const toggle = (id: string, on: boolean) => {
    const next = on ? [...enabled, id] : enabled.filter((x) => x !== id);
    setEnabled(next, persistConnectors);
  };

  const activeCount = connectors.filter((s) => enabled.includes(s.id)).length;

  const itemCls =
    "flex items-center justify-between gap-3 w-full px-3 py-2 text-sm text-space hover:bg-space-10 transition-colors text-left";

  return (
    <div className="relative shrink-0" ref={ref}>
      {/* "+" trigger — replaces the old paperclip attach button. */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="p-2 rounded-xl text-space-40 hover:text-jove hover:bg-jove-10 transition-colors"
        title="Add files or connectors"
        aria-label="Add files or connectors"
        aria-expanded={open}
      >
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75} aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
        </svg>
      </button>

      {open && (
        <div
          className="absolute bottom-full left-0 mb-2 w-60 rounded-lg border border-space-20 bg-surface shadow-lg py-1 z-50 animate-[scaleIn_120ms_ease-out]"
          role="menu"
        >
          <button
            type="button"
            role="menuitem"
            className={itemCls}
            onClick={() => {
              close();
              onAddFiles();
            }}
          >
            <span className="flex items-center gap-2">
              <svg className="h-4 w-4 text-space-50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75} aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
              </svg>
              Add files or photos
            </span>
          </button>

          {/* Connectors — nested flyout submenu (Anthropic-style). */}
          <div
            className="relative"
            onMouseEnter={() => setSubOpen(true)}
            onMouseLeave={() => setSubOpen(false)}
          >
            <button
              type="button"
              role="menuitem"
              aria-haspopup="menu"
              aria-expanded={subOpen}
              className={itemCls}
              onClick={() => setSubOpen((v) => !v)}
            >
              <span className="flex items-center gap-2">
                <svg className="h-4 w-4 text-space-50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75} aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Connectors
                {activeCount > 0 && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-jove-10 text-jove font-medium">
                    {activeCount}
                  </span>
                )}
              </span>
              <svg className="h-3.5 w-3.5 text-space-40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>

            {subOpen && (
              <div
                /* Desktop: a right flyout (`left-full`). On phones that runs
                   off the right edge, so below `sm` it stacks above the
                   Connectors row, left-aligned, growing upward within the
                   composer's open menu space. */
                className="absolute left-0 bottom-full mb-1 w-64
                           sm:left-full sm:bottom-0 sm:mb-0 sm:ml-1
                           max-h-80 overflow-y-auto rounded-lg border border-space-20 bg-surface shadow-lg py-1 z-50"
                role="menu"
              >
                {isLoading && servers.length === 0 ? (
                  <p className="flex items-center gap-2 px-3 py-2 text-[11px] text-space-40 leading-snug">
                    <Spinner size="sm" className="text-space-40" />
                    Loading connectors…
                  </p>
                ) : connectors.length === 0 ? (
                  <p className="px-3 py-2 text-[11px] text-space-40 leading-snug">
                    No connectors enabled in Settings.
                  </p>
                ) : (
                  connectors.map((s) => {
                    const on = enabled.includes(s.id);
                    const connected = s.status === "connected" || s.status === "running";
                    return (
                      <button
                        key={s.id}
                        type="button"
                        role="menuitemcheckbox"
                        aria-checked={on}
                        className={itemCls}
                        onClick={() => toggle(s.id, !on)}
                      >
                        <span className="flex items-center gap-2 min-w-0">
                          <span
                            className={`inline-block h-2 w-2 rounded-full shrink-0 ${connected ? "bg-emerald-500" : "bg-space-40"}`}
                            aria-hidden
                          />
                          <span className="truncate">{s.name}</span>
                        </span>
                        {/* Toggle pill */}
                        <span
                          className={`relative h-4 w-7 shrink-0 rounded-full transition-colors ${on ? "bg-jove" : "bg-space-30"}`}
                        >
                          <span className={`absolute top-0.5 h-3 w-3 rounded-full bg-surface transition-all ${on ? "left-3.5" : "left-0.5"}`} />
                        </span>
                      </button>
                    );
                  })
                )}
                <div className="border-t border-space-10 mt-1 pt-1">
                  <button
                    type="button"
                    role="menuitem"
                    className={`${itemCls} text-space-50`}
                    onClick={() => {
                      close();
                      navigate("/settings");
                    }}
                  >
                    <span className="flex items-center gap-2">
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75} aria-hidden>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      Manage connectors
                    </span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
